import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Search, ShoppingBag, Menu, X, Instagram, MessageCircle, User } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useCart } from "../contexts/CartContext";
import CartDrawer from "./CartDrawer";

const Layout = () => {
  const { itemCount, setOpen } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [cookieAccepted, setCookieAccepted] = useState(
    () => localStorage.getItem("dripchexk_cookie") === "1"
  );
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!location.hash) window.scrollTo(0, 0);
    setMobileOpen(false);
  }, [location.pathname]);

  // Handle #reviews anchor scroll
  useEffect(() => {
    if (location.hash === "#reviews") {
      setTimeout(() => {
        const el = document.getElementById("reviews");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [location.hash, location.pathname]);

  const goReviews = (e) => {
    e.preventDefault();
    setMobileOpen(false);
    if (location.pathname === "/") {
      const el = document.getElementById("reviews");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate("/#reviews");
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Announcement bar */}
      <div
        data-testid="announcement-bar"
        className="bg-[#FF69B4] text-black text-[10px] sm:text-[11px] tracking-[0.35em] uppercase font-semibold text-center py-2"
      >
        Free Shipping on Orders Above ₹1,999 ✦
      </div>

      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/80 border-b border-[#222]">
        <div className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12 h-14 sm:h-16 flex items-center justify-between">
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 -ml-2"
            onClick={() => setMobileOpen((o) => !o)}
            aria-label="Menu"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>

          <Link
            to="/"
            data-testid="brand-logo"
            className="flex-1 lg:flex-none text-center lg:text-left"
          >
            <span className="font-display text-lg sm:text-xl tracking-[0.15em]">
              THE DRIPCHEXK<span className="text-[#FF69B4]">.</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-10 absolute left-1/2 -translate-x-1/2">
            <NavLink
              to="/"
              end
              data-testid="nav-home"
              className={({ isActive }) =>
                `tiny-link hover:text-[#FF69B4] transition-colors ${isActive ? "text-white" : "text-white/60"}`
              }
            >
              Home
            </NavLink>
            <NavLink
              to="/collections"
              data-testid="nav-collections"
              className={({ isActive }) =>
                `tiny-link hover:text-[#FF69B4] transition-colors ${isActive ? "text-white" : "text-white/60"}`
              }
            >
              Collections
            </NavLink>
            <a
              href="/#reviews"
              onClick={goReviews}
              data-testid="nav-reviews"
              className="tiny-link text-white/60 hover:text-[#FF69B4] transition-colors"
            >
              Reviews
            </a>
            <NavLink
              to="/contact"
              data-testid="nav-contact"
              className={({ isActive }) =>
                `tiny-link hover:text-[#FF69B4] transition-colors ${isActive ? "text-white" : "text-white/60"}`
              }
            >
              Contact
            </NavLink>
          </nav>

          <div className="flex items-center gap-1 sm:gap-2">
            <NavLink
              to="/account"
              data-testid="account-link"
              className="p-2 hover:text-[#FF69B4] transition-colors"
              aria-label="My Orders"
            >
              <User size={18} />
            </NavLink>
            <button
              data-testid="search-toggle"
              onClick={() => setSearchOpen((o) => !o)}
              className="p-2 hover:text-[#FF69B4] transition-colors"
              aria-label="Search"
            >
              <Search size={18} />
            </button>
            <button
              data-testid="cart-toggle"
              onClick={() => setOpen(true)}
              className="p-2 relative hover:text-[#FF69B4] transition-colors"
              aria-label="Cart"
            >
              <ShoppingBag size={18} />
              {itemCount > 0 && (
                <span
                  data-testid="cart-count"
                  className="absolute -top-0.5 -right-0.5 bg-[#FF69B4] text-black text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold"
                >
                  {itemCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Search bar */}
        <AnimatePresence>
          {searchOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="border-t border-[#222] overflow-hidden bg-[#0a0a0a]"
            >
              <div className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12 py-4">
                <input
                  data-testid="search-input"
                  type="search"
                  placeholder="Search hoodies, tees..."
                  className="w-full bg-transparent border-b border-[#333] py-3 text-sm focus:outline-none focus:border-[#FF69B4] transition-colors"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      const q = e.target.value.trim().toLowerCase();
                      if (q) window.location.href = `/collections?q=${encodeURIComponent(q)}`;
                    }
                  }}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Mobile nav */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="lg:hidden border-t border-[#222] bg-black"
            >
              <nav className="flex flex-col px-6 py-2">
                <NavLink to="/" end data-testid="mobile-nav-home" className="py-4 tiny-link border-b border-[#1a1a1a]">Home</NavLink>
                <NavLink to="/collections" data-testid="mobile-nav-collections" className="py-4 tiny-link border-b border-[#1a1a1a]">Collections</NavLink>
                <a href="/#reviews" onClick={goReviews} data-testid="mobile-nav-reviews" className="py-4 tiny-link border-b border-[#1a1a1a]">Reviews</a>
                <NavLink to="/contact" data-testid="mobile-nav-contact" className="py-4 tiny-link border-b border-[#1a1a1a]">Contact</NavLink>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <main className="fade-in" key={location.pathname}>
        <Outlet />
      </main>

      <Footer />

      <CartDrawer />

      {/* Floating WhatsApp */}
      <a
        data-testid="whatsapp-float"
        href="https://wa.me/918383862206"
        target="_blank"
        rel="noreferrer noopener"
        className="fixed bottom-6 right-6 z-30 w-14 h-14 rounded-full bg-[#25D366] hover:scale-110 transition-transform flex items-center justify-center shadow-2xl"
        aria-label="WhatsApp us"
      >
        <MessageCircle className="text-black" size={24} />
      </a>

      {/* Cookie consent */}
      <AnimatePresence>
        {!cookieAccepted && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            data-testid="cookie-banner"
            className="fixed bottom-0 left-0 right-0 z-40 bg-black/95 backdrop-blur border-t border-[#222] p-4 sm:p-5"
          >
            <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center gap-4 text-sm">
              <p className="text-white/70 flex-1 text-center sm:text-left">
                We use cookies to improve your experience. By using the site you agree to our{" "}
                <Link to="/privacy" className="underline hover:text-[#FF69B4]">Privacy Policy</Link> (IT Act 2000 compliant).
              </p>
              <button
                data-testid="cookie-accept"
                onClick={() => {
                  localStorage.setItem("dripchexk_cookie", "1");
                  setCookieAccepted(true);
                }}
                className="pink-cta tiny-link px-6 py-3"
              >
                Accept
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Footer = () => (
  <footer className="border-t border-[#222] mt-24 bg-black">
    <div className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12 py-16 grid grid-cols-2 md:grid-cols-4 gap-10">
      <div className="col-span-2">
        <p className="font-display text-2xl sm:text-3xl tracking-[0.15em]">
          THE DRIPCHEXK<span className="text-[#FF69B4]">.</span>
        </p>
        <p className="tiny-link text-white/40 mt-2">The Drip is Real.</p>
        <p className="text-white/60 text-sm mt-6 max-w-sm leading-relaxed">
          Premium streetwear from India. Heavyweight hoodies, statement tees, COD available across the country.
        </p>
        <div className="flex gap-3 mt-6">
          <a href="https://instagram.com/thedripchexk" target="_blank" rel="noreferrer" className="p-2 border border-[#222] hover:border-[#FF69B4] hover:text-[#FF69B4] transition-colors" aria-label="Instagram"><Instagram size={16} /></a>
          <a href="https://wa.me/918383862206" target="_blank" rel="noreferrer" className="p-2 border border-[#222] hover:border-[#FF69B4] hover:text-[#FF69B4] transition-colors" aria-label="WhatsApp"><MessageCircle size={16} /></a>
        </div>
      </div>
      <div>
        <p className="tiny-link text-white/40 mb-4">Shop</p>
        <ul className="space-y-2 text-sm">
          <li><Link to="/" className="text-white/70 hover:text-[#FF69B4] transition-colors">Home</Link></li>
          <li><Link to="/collections" className="text-white/70 hover:text-[#FF69B4] transition-colors">Collections</Link></li>
          <li><Link to="/about" className="text-white/70 hover:text-[#FF69B4] transition-colors">About</Link></li>
          <li><Link to="/contact" className="text-white/70 hover:text-[#FF69B4] transition-colors">Contact</Link></li>
        </ul>
      </div>
      <div>
        <p className="tiny-link text-white/40 mb-4">Help</p>
        <ul className="space-y-2 text-sm">
          <li><Link to="/faq" className="text-white/70 hover:text-[#FF69B4] transition-colors">FAQ</Link></li>
          <li><Link to="/size-guide" className="text-white/70 hover:text-[#FF69B4] transition-colors">Size Guide</Link></li>
          <li><Link to="/shipping" className="text-white/70 hover:text-[#FF69B4] transition-colors">Shipping Policy</Link></li>
          <li><Link to="/returns" className="text-white/70 hover:text-[#FF69B4] transition-colors">Returns</Link></li>
          <li><Link to="/privacy" className="text-white/70 hover:text-[#FF69B4] transition-colors">Privacy Policy</Link></li>
          <li><Link to="/terms" className="text-white/70 hover:text-[#FF69B4] transition-colors">Terms & Conditions</Link></li>
        </ul>
      </div>
    </div>
    <div className="border-t border-[#222] px-5 sm:px-8 lg:px-12 py-6 flex flex-col sm:flex-row gap-2 justify-between text-xs text-white/40">
      <p>© 2026 The Dripchexk. All rights reserved.</p>
      <p>thedripchexk@gmail.com · +91 8383862206 · Made in India</p>
    </div>
  </footer>
);

export default Layout;
